import { useMemo } from "react";
import { useAdminViewState } from "../../lib/admin/adminViewState";

export type SortDir = "asc" | "desc";
export type SortState<K extends string> = { key: K; dir: SortDir };

/**
 * Сортировка таблиц админки. Значение колонки достаётся через `pick`:
 * строки сравниваем локалью, числа и даты — численно, пустые всегда снизу.
 */
export function useTableSort<Row, K extends string>(
  rows: Row[],
  pick: (row: Row, key: K) => string | number | null | undefined,
  initial: SortState<K>,
  /** Ключ памяти админки: сортировка переживает уход на доску и «Назад». */
  viewKey: string | null = null
) {
  const [sort, setSort] = useAdminViewState<SortState<K>>(viewKey, initial);

  const sorted = useMemo(() => {
    const out = [...rows];
    out.sort((a, b) => {
      const av = pick(a, sort.key);
      const bv = pick(b, sort.key);
      const aEmpty = av === null || av === undefined || av === "";
      const bEmpty = bv === null || bv === undefined || bv === "";
      if (aEmpty && bEmpty) return 0;
      if (aEmpty) return 1;
      if (bEmpty) return -1;
      let cmp: number;
      if (typeof av === "number" && typeof bv === "number") {
        cmp = av - bv;
      } else {
        cmp = String(av).localeCompare(String(bv), "ru");
      }
      return sort.dir === "asc" ? cmp : -cmp;
    });
    return out;
    // pick стабилен у всех вызывающих (модульная функция или useCallback).
  }, [rows, sort, pick]);

  function toggle(key: K) {
    setSort((prev) =>
      prev.key === key ? { key, dir: prev.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }
    );
  }

  return { sort, sorted, toggle };
}

type ThProps<K extends string> = {
  label: string;
  sortKey?: K;
  sort?: SortState<K>;
  onSort?: (key: K) => void;
  title?: string;
};

/** Заголовок колонки. Без `sortKey` — обычный `th`. */
export function SortableTh<K extends string>({
  label,
  sortKey,
  sort,
  onSort,
  title,
}: ThProps<K>) {
  if (!sortKey || !onSort) {
    return <th title={title}>{label}</th>;
  }
  const active = sort?.key === sortKey;
  return (
    <th title={title}>
      <button
        type="button"
        className={
          active
            ? "wb-admin-table__sort wb-admin-table__sort--active"
            : "wb-admin-table__sort"
        }
        onClick={() => onSort(sortKey)}
        aria-label={`Сортировать по «${label}»`}
      >
        {label}
        <span aria-hidden="true" className="wb-admin-table__sort-arrow">
          {active ? (sort!.dir === "asc" ? "▲" : "▼") : "↕"}
        </span>
      </button>
    </th>
  );
}
